import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, categoryApi, fileApi } from '@/api/endpoints';
import { Alert, ErrorState, Loading } from '@/components/common/State';
import MediaAttach from '@/components/common/MediaAttach';
import { useMediaUpload } from '@/components/common/useMediaUpload';
import { draftFromSaved, toMediaInputs, type DraftMedia } from '@/utils/media';
import type {
  Category,
  IngredientInput,
  ProductDetail,
  ProductOptionInput,
  ProductSavePayload,
  ProductStatus,
} from '@/api/types';

const STATUS_LABEL: Record<ProductStatus, string> = {
  DRAFT: '임시저장',
  ON_SALE: '판매중',
  SOLD_OUT: '품절',
  HIDDEN: '숨김',
};

interface FormState {
  name: string;
  slug: string;
  brand: string;
  summary: string;
  description: string;
  volume: string;
  price: string;
  discountPrice: string;
  thumbnailUrl: string;
  status: ProductStatus;
  featured: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  slug: '',
  brand: '',
  summary: '',
  description: '',
  volume: '',
  price: '',
  discountPrice: '',
  thumbnailUrl: '',
  status: 'DRAFT',
  featured: false,
};

/** 상품 상세 미디어(이미지·동영상) 최대 개수 */
const MAX_PRODUCT_MEDIA = 20;

const EMPTY_OPTION: ProductOptionInput = {
  name: '',
  sku: '',
  additionalPrice: 0,
  stockQuantity: 0,
  safetyStock: 0,
  sortOrder: 0,
  active: true,
};

/** 트리 구조의 카테고리를 select 에 넣을 수 있게 깊이 표시와 함께 펼친다. */
function flattenCategories(nodes: Category[], depth = 0): { id: number; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.categoryId, label: `${'　'.repeat(depth)}${node.name}` },
    ...flattenCategories(node.children ?? [], depth + 1),
  ]);
}

export default function AdminProductFormPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = productId !== undefined;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // 배열 순서가 그대로 의미를 갖는다. 첫 번째가 대표 카테고리.
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [options, setOptions] = useState<ProductOptionInput[]>([{ ...EMPTY_OPTION }]);
  const [media, setMedia] = useState<DraftMedia[]>([]);
  // 대표 이미지도 고르는 즉시 올리지 않고 저장할 때 올린다.
  const [thumbnailFile, setThumbnailFile] = useState<{ file: File; previewUrl: string } | null>(null);
  const [ingredients, setIngredients] = useState<IngredientInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const uploader = useMediaUpload('products');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const categories = useQuery({ queryKey: ['categories'], queryFn: categoryApi.tree });
  const categoryOptions = useMemo(
    () => flattenCategories(categories.data ?? []),
    [categories.data],
  );

  const detail = useQuery({
    queryKey: ['admin', 'product', Number(productId)],
    queryFn: () => adminApi.productDetail(Number(productId)),
    enabled: isEdit,
  });

  // 수정 모드에서는 불러온 상품으로 폼을 채운다.
  useEffect(() => {
    const product: ProductDetail | undefined = detail.data;
    if (!product) return;
    setForm({
      name: product.name,
      slug: product.slug,
      brand: product.brand ?? '',
      summary: product.summary ?? '',
      description: product.description ?? '',
      volume: product.volume ?? '',
      price: String(product.price),
      discountPrice: product.discountPrice != null ? String(product.discountPrice) : '',
      thumbnailUrl: product.thumbnailUrl ?? '',
      status: product.status,
      featured: product.featured,
    });
    // 서버가 대표를 첫 번째로 내려 준다.
    setCategoryIds(product.categories.map((category) => category.categoryId));
    setOptions(
      product.options.map((option, index) => ({
        optionId: option.optionId,
        name: option.name,
        sku: option.sku,
        additionalPrice: option.additionalPrice,
        stockQuantity: option.stockQuantity,
        safetyStock: option.safetyStock,
        sortOrder: index,
        active: option.active,
      })),
    );
    setMedia(product.media.map(draftFromSaved));
    setIngredients(
      product.ingredients.map((item, index) => ({
        name: item.name,
        content: item.content ?? '',
        description: item.description ?? '',
        sortOrder: index,
      })),
    );
  }, [detail.data]);

  // 대표 이미지 미리보기용 blob: URL 은 바뀌거나 화면을 떠날 때 놓아준다.
  useEffect(() => {
    if (!thumbnailFile) return;
    return () => URL.revokeObjectURL(thumbnailFile.previewUrl);
  }, [thumbnailFile]);

  const save = useMutation({
    mutationFn: async (base: Omit<ProductSavePayload, 'media'>) => {
      // 저장 직전에 대표 이미지와 상세 미디어를 S3 에 올린다.
      let thumbnailUrl = base.thumbnailUrl;
      if (thumbnailFile) {
        setUploadingThumbnail(true);
        try {
          thumbnailUrl = (await fileApi.upload(thumbnailFile.file, 'products')).url;
        } finally {
          setUploadingThumbnail(false);
        }
        // 저장이 실패해 다시 눌러도 같은 파일을 두 번 올리지 않도록 URL 로 바꿔 둔다.
        setThumbnailFile(null);
        setForm((prev) => ({ ...prev, thumbnailUrl: thumbnailUrl ?? '' }));
      }
      const uploaded = await uploader.upload(media);
      setMedia(uploaded);

      const payload: ProductSavePayload = { ...base, thumbnailUrl, media: toMediaInputs(uploaded) };
      return isEdit ? adminApi.updateProduct(Number(productId), payload) : adminApi.createProduct(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'product', saved.productId] });
      navigate('/admin/products');
    },
    onError: (e: Error) => setError(e.message),
  });

  const change =
    <K extends keyof FormState>(key: K) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: event.target.value } as FormState));

  const categoryName = (id: number) =>
    categoryOptions.find((category) => category.id === id)?.label.trim() ?? `#${id}`;

  const toggleCategory = (id: number) =>
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  /** 선택 목록의 맨 앞으로 옮기면 대표가 된다. */
  const makePrimary = (id: number) =>
    setCategoryIds((prev) => [id, ...prev.filter((x) => x !== id)]);

  const patchOption = (index: number, patch: Partial<ProductOptionInput>) =>
    setOptions((prev) => prev.map((option, i) => (i === index ? { ...option, ...patch } : option)));

  const patchIngredient = (index: number, patch: Partial<IngredientInput>) =>
    setIngredients((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (categoryIds.length === 0) {
      setError('카테고리를 하나 이상 선택하세요.');
      return;
    }
    const valid = options.filter((option) => option.name.trim() && option.sku.trim());
    if (valid.length === 0) {
      setError('옵션을 최소 하나 이상 등록해야 합니다. (옵션명과 SKU 필수)');
      return;
    }

    const payload: Omit<ProductSavePayload, 'media'> = {
      categoryIds,
      name: form.name.trim(),
      brand: form.brand.trim() || undefined,
      summary: form.summary.trim() || undefined,
      description: form.description.trim() || undefined,
      volume: form.volume.trim() || undefined,
      price: Number(form.price),
      discountPrice: form.discountPrice ? Number(form.discountPrice) : undefined,
      thumbnailUrl: form.thumbnailUrl.trim() || undefined,
      status: form.status,
      featured: form.featured,
      ingredients: ingredients
        .filter((item) => item.name.trim())
        .map((item, index) => ({ ...item, sortOrder: index })),
      options: valid.map((option, index) => ({ ...option, sortOrder: index })),
    };
    // 수정 API 는 slug 를 받지 않는다. 등록일 때만 실어 보낸다.
    if (!isEdit) {
      payload.slug = form.slug.trim();
    }
    save.mutate(payload);
  }

  if (isEdit && detail.isLoading) return <Loading />;
  if (isEdit && detail.isError) return <ErrorState error={detail.error} onRetry={detail.refetch} />;

  return (
    <>
      <h1 className="panel-title">{isEdit ? `상품 수정 #${productId}` : '상품 등록'}</h1>
      {error && <Alert tone="error">{error}</Alert>}
      {uploadingThumbnail && <Alert tone="info">대표 이미지를 업로드하는 중입니다…</Alert>}
      {uploader.progress != null && (
        <Alert tone="info">상세 이미지·동영상을 업로드하는 중입니다… {uploader.progress}%</Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>기본 정보</h2>

            <div className="field">
              <label>카테고리 * (여러 개 선택 가능)</label>
              <div
                style={{
                  border: '1px solid var(--gray-300)',
                  borderRadius: 4,
                  padding: 10,
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                {categoryOptions.map((category) => (
                  <label
                    key={category.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontWeight: 400 }}
                  >
                    <input
                      type="checkbox"
                      checked={categoryIds.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                    />
                    <span style={{ whiteSpace: 'pre' }}>{category.label}</span>
                  </label>
                ))}
              </div>

              {categoryIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {categoryIds.map((id, index) => (
                    <span
                      key={id}
                      className={index === 0 ? 'badge badge--success' : 'badge badge--muted'}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {index === 0 && '대표 · '}
                      {categoryName(id)}
                      {index > 0 && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          style={{ padding: '0 4px' }}
                          onClick={() => makePrimary(id)}
                        >
                          대표로
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              <span className="muted" style={{ fontSize: 12 }}>
                맨 앞이 대표 카테고리로, 목록·상세에 표시됩니다. 상위 카테고리로 검색하면 하위까지 함께 조회됩니다.
              </span>
            </div>

            <div className="field">
              <label htmlFor="name">상품명 *</label>
              <input id="name" className="input" required maxLength={200} value={form.name} onChange={change('name')} />
            </div>

            <div className="field">
              <label htmlFor="slug">URL 식별자(slug) {isEdit ? '(수정 불가)' : '*'}</label>
              <input
                id="slug"
                className="input"
                required={!isEdit}
                disabled={isEdit}
                maxLength={200}
                placeholder="cgf-regeneration-ampoule"
                value={form.slug}
                onChange={change('slug')}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label htmlFor="brand">브랜드</label>
                <input id="brand" className="input" maxLength={100} value={form.brand} onChange={change('brand')} />
              </div>
              <div className="field">
                <label htmlFor="volume">용량</label>
                <input
                  id="volume"
                  className="input"
                  maxLength={50}
                  placeholder="30ml"
                  value={form.volume}
                  onChange={change('volume')}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="summary">한줄 소개</label>
              <input
                id="summary"
                className="input"
                maxLength={500}
                value={form.summary}
                onChange={change('summary')}
              />
            </div>

            <div className="field">
              <label htmlFor="description">상세 설명</label>
              <textarea
                id="description"
                className="textarea"
                value={form.description}
                onChange={change('description')}
              />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>가격 · 노출</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label htmlFor="price">정가 *</label>
                <input
                  id="price"
                  className="input"
                  type="number"
                  min={0}
                  required
                  value={form.price}
                  onChange={change('price')}
                />
              </div>
              <div className="field">
                <label htmlFor="discountPrice">할인가</label>
                <input
                  id="discountPrice"
                  className="input"
                  type="number"
                  min={0}
                  value={form.discountPrice}
                  onChange={change('discountPrice')}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="status">판매 상태 *</label>
              <select id="status" className="select" value={form.status} onChange={change('status')}>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="featured">메인 추천 노출</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                <input
                  id="featured"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.checked }))}
                />
                <span className="muted">
                  메인 화면 추천 영역에 노출합니다. 헤더의 &lsquo;베스트&rsquo;는 판매량 자동 집계라 별개입니다.
                </span>
              </label>
            </div>

            <div className="field">
              <label htmlFor="thumbnail">대표 이미지</label>
              {(thumbnailFile || form.thumbnailUrl) && (
                <img
                  src={thumbnailFile?.previewUrl ?? form.thumbnailUrl}
                  alt="대표 이미지 미리보기"
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6 }}
                />
              )}
              <input
                id="thumbnail"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setThumbnailFile({ file, previewUrl: URL.createObjectURL(file) });
                  }
                  e.target.value = '';
                }}
              />
              <input
                className="input"
                placeholder="또는 이미지 URL 직접 입력"
                value={thumbnailFile ? '' : form.thumbnailUrl}
                onChange={(e) => {
                  // URL 을 직접 넣으면 골라 둔 파일은 버린다.
                  setThumbnailFile(null);
                  change('thumbnailUrl')(e);
                }}
              />
              {thumbnailFile && <span className="muted">{thumbnailFile.file.name} — 저장할 때 업로드됩니다.</span>}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>옵션 · 재고</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              여기서 입력한 재고 수량은 그대로 반영됩니다. 입고·실사처럼 이력을 남겨야 하는 변동은 상품 목록의
              &lsquo;옵션/재고&rsquo; 화면을 쓰세요. 목록에서 뺀 옵션은 삭제되지 않고 비활성 처리됩니다.
            </p>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>옵션명 *</th>
                    <th>SKU *</th>
                    <th>추가금</th>
                    <th>재고</th>
                    <th>안전재고</th>
                    <th>판매</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {options.map((option, index) => (
                    <tr key={option.optionId ?? `new-${index}`}>
                      <td>
                        <input
                          className="input"
                          value={option.name}
                          onChange={(e) => patchOption(index, { name: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          maxLength={60}
                          value={option.sku}
                          onChange={(e) => patchOption(index, { sku: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          style={{ width: 100 }}
                          value={option.additionalPrice}
                          onChange={(e) => patchOption(index, { additionalPrice: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          style={{ width: 90 }}
                          value={option.stockQuantity}
                          onChange={(e) => patchOption(index, { stockQuantity: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          style={{ width: 90 }}
                          value={option.safetyStock}
                          onChange={(e) => patchOption(index, { safetyStock: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={option.active}
                          onChange={(e) => patchOption(index, { active: e.target.checked })}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setOptions((prev) => prev.filter((_, i) => i !== index))}
                        >
                          제거
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setOptions((prev) => [...prev, { ...EMPTY_OPTION, sortOrder: prev.length }])}
            >
              옵션 추가
            </button>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>상세 이미지 · 동영상</h2>

            <MediaAttach
              value={media}
              onChange={setMedia}
              max={MAX_PRODUCT_MEDIA}
              label="파일 선택 — 저장할 때 업로드됩니다"
              size={120}
              progress={uploader.progress}
            />
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>주요 성분</h2>

            {ingredients.map((item, index) => (
              <div
                key={index}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 8, marginBottom: 8 }}
              >
                <input
                  className="input"
                  placeholder="성분명"
                  value={item.name}
                  onChange={(e) => patchIngredient(index, { name: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="함량"
                  value={item.content ?? ''}
                  onChange={(e) => patchIngredient(index, { content: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="설명"
                  value={item.description ?? ''}
                  onChange={(e) => patchIngredient(index, { description: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setIngredients((prev) => prev.filter((_, i) => i !== index))}
                >
                  제거
                </button>
              </div>
            ))}

            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() =>
                setIngredients((prev) => [...prev, { name: '', content: '', description: '', sortOrder: prev.length }])
              }
            >
              성분 추가
            </button>
          </div>
        </div>

        <div className="form-actions form-actions--sticky">
          <button type="button" className="btn btn--outline" onClick={() => navigate('/admin/products')}>
            취소
          </button>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? '저장 중…' : isEdit ? '수정 저장' : '상품 등록'}
          </button>
        </div>
      </form>
    </>
  );
}
